/**
 * Rob Steins: live photo preview for the personalized photo charms (see snippets/rs-photo-preview.liquid).
 *
 * The upload box on these products belongs to the Lite Upload app. This script does not touch the upload itself: it only
 * listens for the file the customer picks (or drops), reads it in the browser, and paints it into the charm's photo
 * areas on the main product image, cropped to fill each area (heart, circle or the sample "your photo" square).
 * It also jumps the gallery back to that image, and paints the photo again if Horizon redraws the gallery.
 *
 * window.rsPhotoPreview(file | url | null) shows (or, with null, clears) a preview by hand.
 */

const config = document.querySelector('rs-photo-preview');

if (config instanceof HTMLElement && !window.rsPhotoPreview) {
  /** @type {{shape: 'heart'|'circle'|'rect', x: number, y: number, w: number, h: number, r?: number, pts?: number[][]}[]} */
  const areas = JSON.parse(config.dataset.areas || '[]');
  const imageName = config.dataset.image || '';
  let photoUrl = '';
  let objectUrl = '';
  /** Counts previews, so a slower earlier photo can never overwrite (or clear) a newer one. */
  let latest = 0;

  /** The main product image: the gallery slide whose picture is the product's featured image (or the first slide). */
  function findMainImage() {
    const images = [...document.querySelectorAll('media-gallery .product-media img')];
    const byName = imageName ? images.find((img) => (img.currentSrc || img.src).includes(imageName.split('.')[0])) : null;
    return /** @type {HTMLImageElement | undefined} */ (byName || images[0]);
  }

  /** Where the picture is drawn inside its box (the gallery uses object-fit: contain). */
  function drawnRect(img) {
    const box = { w: img.clientWidth, h: img.clientHeight };
    const ratio = img.naturalWidth && img.naturalHeight ? img.naturalWidth / img.naturalHeight : 1;
    let w = box.w;
    let h = w / ratio;
    if (h > box.h) {
      h = box.h;
      w = h * ratio;
    }
    return { left: img.offsetLeft + (box.w - w) / 2, top: img.offsetTop + (box.h - h) / 2, width: w, height: h };
  }

  function clear() {
    document.querySelectorAll('.rs-pp').forEach((el) => el.remove());
  }

  function paint() {
    clear();
    if (!photoUrl) return;
    const img = findMainImage();
    const holder = img?.closest('.product-media');
    if (!img || !(holder instanceof HTMLElement)) return;
    if (!img.complete || !img.naturalWidth) {
      img.addEventListener('load', paint, { once: true });
      return;
    }
    if (getComputedStyle(holder).position === 'static') holder.style.position = 'relative';

    const rect = drawnRect(img);
    const layer = document.createElement('div');
    layer.className = 'rs-pp';
    layer.setAttribute('aria-hidden', 'true');
    Object.assign(layer.style, {
      left: `${rect.left}px`,
      top: `${rect.top}px`,
      width: `${rect.width}px`,
      height: `${rect.height}px`,
    });
    for (const area of areas) {
      const el = document.createElement('div');
      el.className = `rs-pp__area rs-pp__area--${area.shape}`;
      Object.assign(el.style, {
        left: `${area.x}%`,
        top: `${area.y}%`,
        width: `${area.w}%`,
        height: `${area.h}%`,
        backgroundImage: `url("${photoUrl}")`,
        transform: `rotate(${area.r || 0}deg)`,
      });
      el.style.setProperty('--rs-pp-r', `${area.r || 0}deg`);
      // the traced outline of the window, when there is one, is exact; otherwise the heart/circle shape is used
      if (area.pts?.length) el.style.clipPath = `polygon(${area.pts.map(([x, y]) => `${x}% ${y}%`).join(', ')})`;
      layer.append(el);
    }
    const tag = document.createElement('span');
    tag.className = 'rs-pp__tag';
    tag.textContent = 'Your photo preview';
    layer.append(tag);
    holder.append(layer);
  }

  /** Brings the gallery back to the main image, so the preview is in view. */
  function showMainSlide() {
    const img = findMainImage();
    const slide = img?.closest('slideshow-slide');
    const slideshow = img?.closest('slideshow-component');
    if (!slide || !slideshow || typeof slideshow.select !== 'function') return;
    const slides = [...slideshow.querySelectorAll('slideshow-slide')];
    const index = slides.indexOf(/** @type {HTMLElement} */ (slide));
    try {
      if (index > -1) slideshow.select(index, undefined, { animate: true });
    } catch (error) {
      console.warn(error);
    }
  }

  /** @param {File | string | null} source */
  function preview(source) {
    const turn = ++latest;
    const previousObjectUrl = objectUrl;
    objectUrl = '';
    if (!source) {
      if (previousObjectUrl) URL.revokeObjectURL(previousObjectUrl);
      photoUrl = '';
      clear();
      return;
    }
    if (typeof source === 'string') {
      photoUrl = source;
    } else {
      if (!source.type.startsWith('image/')) return;
      objectUrl = URL.createObjectURL(source);
      photoUrl = objectUrl;
    }
    // Only paint once the browser can actually show the photo (some phone formats, like HEIC, it cannot everywhere).
    const test = new Image();
    test.onload = () => {
      if (turn !== latest) return;
      if (previousObjectUrl) URL.revokeObjectURL(previousObjectUrl);
      showMainSlide();
      paint();
    };
    test.onerror = () => {
      if (turn !== latest) return;
      photoUrl = '';
      clear();
    };
    test.src = photoUrl;
  }
  window.rsPhotoPreview = preview;

  /** @param {FileList | null | undefined} files */
  const firstImage = (files) => [...(files || [])].find((file) => file.type.startsWith('image/'));

  document.addEventListener(
    'change',
    (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || input.type !== 'file') return;
      if (input.closest('rs-photo-builder')) return; // the builder sends its own cropped photo
      const file = firstImage(input.files);
      if (file) preview(file);
    },
    true
  );

  document.addEventListener(
    'drop',
    (event) => {
      const zone = event.target instanceof Element ? event.target.closest('#lite-upload-drop-zone, .lite-upload-wrapper') : null;
      if (!zone) return;
      const file = firstImage(event.dataTransfer?.files);
      if (file) preview(file);
    },
    true
  );

  // Horizon redraws the gallery (variant changes, zoom); paint again when the preview goes missing, and keep it sized.
  let queued = false;
  const repaint = () => {
    if (queued || !photoUrl) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      const img = findMainImage();
      if (!img?.closest('.product-media')?.querySelector(':scope > .rs-pp')) paint();
    });
  };
  const gallery = document.querySelector('media-gallery')?.parentElement;
  if (gallery) new MutationObserver(repaint).observe(gallery, { childList: true, subtree: true });
  window.addEventListener('resize', () => photoUrl && requestAnimationFrame(paint));
}
